import { UserManager } from "oidc-client";
import { config } from "../config/index";
import {
	openIdCallbackSuffix, 
	openIdSilentRenewPath,
	maxAuthClockSkewSeconds
} from "constants.js";
import {
	getAuthenticationErrorDetail,
	isSigninRequiredError
} from "./oidc/authenticationError";
import { SigninRedirectGuard } from "./oidc/signinRedirectGuard";
import { SilentRenewRecovery } from "./oidc/silentRenewRecovery";

const baseUrl = `${location.protocol}//${location.hostname}${(location.port ? ":" + location.port : "")}`;

const oidcSettings = {
	authority: config.ssoUrl,
	client_id: "vesl-web.pkce",
	redirect_uri: `${baseUrl}${openIdCallbackSuffix}`,
	silent_redirect_uri: `${baseUrl}${openIdSilentRenewPath}`, // optional fallback; not required when refresh_token is present
	post_logout_redirect_uri: baseUrl,
	response_type: "code", // auth code + PKCE
	scope: "openid profile email api offline_access", // offline_access requests a refresh token
	automaticSilentRenew: true, // will use refresh_token first if one exists
	clockSkew: maxAuthClockSkewSeconds
};

class OpenIdService {
	constructor($state, $window, $logger) {
		this.$window = $window;
		this.$state = $state;
		this.$logger = $logger;
		this.renewalPromise = null;
		this.localStorage = this.getLocalStorage();
		this.signinRedirectGuard = new SigninRedirectGuard(this.localStorage, this.$logger);
		this.silentRenewRecovery = new SilentRenewRecovery({
			$window: this.$window,
			$logger: this.$logger,
			isSigninRequiredError,
			renewAccessToken: () => this.renewAccessToken()
		});

		this.oidcProvider = new UserManager(oidcSettings);
		window.vesl.logout = () => this.oidcProvider.signoutRedirect();

		this.oidcProvider.events.addSilentRenewError((e) => {
			this.handleSilentRenewError(e);
		});

		this.oidcProvider.clearStaleState();
	}

	logout = () => {
		return this.oidcProvider.signoutRedirect();
	}

	beginSigninRedirect = (routeName, routeParams) => {
		if(!this.signinRedirectGuard.tryRecordAttempt()) {
			return Promise.reject(new Error("Authentication retry limit reached"));
		}

		this.setFromRoute(routeName, routeParams);

		return this.oidcProvider.signinRedirect({ useReplaceToNavigate: true });
	}

	completeSigninRedirect = () => {
		return this.oidcProvider.signinRedirectCallback()
			.then(user => {
				this.signinRedirectGuard.clear();
				this.clearFromRoute();

				return user;
			});
	}

	renewAccessToken = () => {
		if(this.renewalPromise) {
			return this.renewalPromise;
		}

		this.renewalPromise = this.oidcProvider.getUser()
			.then(user => {
				if(!user) {
					throw new Error("No signed-in user is available to renew");
				}

				return this.oidcProvider.signinSilent();
			})
			.then(user => {
				this.silentRenewRecovery.recordRenewalSucceeded();

				return user;
			})
			.finally(() => {
				this.renewalPromise = null;
			});

		return this.renewalPromise;
	}

	handleSilentRenewError = (error) => {
		this.$logger.exception(error, "Session renewal error");
		this.silentRenewRecovery.handleError(error);
	}

	getUserManager = () => this.oidcProvider;

	getAccessToken = () => this.oidcProvider.getUser()
		.then(user => {
			if(!user || !user.access_token) {
				return "";
			}

			if(user.expired) {
				return this.renewAccessToken()
					.then(renewedUser => (renewedUser || {}).access_token || "");
			}

			return user.access_token;
		});

	setFromRoute = (routeName, routeParams) => {
		const fromRoute = JSON.stringify({ name: routeName, params: routeParams });

		try {
			this.localStorage?.setItem("loginFromRoute", fromRoute);
		}
		catch(error) {
			this.$logger.exception(error, "Failed to store login route");
		}
	}

	clearFromRoute = () => {
		try {
			this.localStorage?.removeItem("loginFromRoute");
		}
		catch(error) {
			this.$logger.exception(error, "Failed to clear stored login route");
		}
	}

	getFromRoute = () => {
		try {
			const fromRouteJSON = this.localStorage?.getItem("loginFromRoute");
			return fromRouteJSON ? JSON.parse(fromRouteJSON) : null;
		}
		catch(e) {
			this.$logger.exception(e, "Failed to parse stored login route");
			this.clearFromRoute();

			return null;
		}
	};

	getLocalStorage = () => {
		try {
			return this.$window.localStorage;
		}
		catch(error) {
			this.$logger.exception(error, "Local storage is unavailable");

			return null;
		}
	}

	getAuthenticationFailureMessage = (error) => {
		const detail = getAuthenticationErrorDetail(error);

		this.$logger.error(`Authentication failure: ${detail}`);

		return [
			"We couldn't complete sign-in.",
			"This can happen for a variety of reasons - like losing connection, or your device clock, date, or timezone isn't correct.",
			"",
			"Please check that your connection and clock are set correctly, then retry your sign-in.",
			""
		].join("\n");
	}
}

export default angular
	.module("vesl.services.open-id-service", [])
	.service("openIdService", OpenIdService).name;
